import type { Flight, LogbookEntry } from './types';

/**
 * The one order every logbook read uses.
 *
 * A running total and the page it is printed beside have to agree on what
 * "the previous entry" means, or the "to date" column stops being a running
 * total and becomes four unrelated numbers. Dexie's `orderBy('date')` only
 * promises the date: entries sharing a calendar day — four circuits in one
 * afternoon, which is the ordinary case, not the odd one — come back in
 * primary-key order, and a UUID primary key makes that arbitrary. Sorting by
 * date alone in one place and date-then-creation in another is how a column
 * ends up reading 10.32, 4.57, 8.07, 3.94.
 *
 * So the order is defined once, here, and it is total. When the entry's
 * linked `Flight` knows an actual takeoff (or off-block) instant — a track
 * import, or a hand-typed one — same-day entries sort by that, the same
 * clock-time order the flights list itself reads in. An entry whose flight
 * carries neither (or has no linked flight at all, as most callers that omit
 * `flightById` see it) falls back to `createdAt`: import order for a tracked
 * flight, typing order for a hand-written one. `id` breaks whatever tie is
 * left so the same entries always come back in the same sequence rather than
 * reshuffling between renders.
 */
function chronologicalInstant(
  entry: LogbookEntry,
  flightById: ReadonlyMap<string, Flight> | undefined,
): string {
  const flight = flightById?.get(entry.flightId);
  return flight?.takeoff ?? flight?.offBlock ?? entry.createdAt;
}

export function compareEntriesChronologically(
  a: LogbookEntry,
  b: LogbookEntry,
  flightById?: ReadonlyMap<string, Flight>,
): number {
  return (
    a.date.localeCompare(b.date) ||
    chronologicalInstant(a, flightById).localeCompare(chronologicalInstant(b, flightById)) ||
    a.id.localeCompare(b.id)
  );
}

/**
 * The same order as a copy, leaving the caller's array alone.
 *
 * @param flightById  optional — each entry's linked `Flight`, keyed by id,
 *                     so same-day entries can sort by actual takeoff time
 *                     rather than just `createdAt`. Omit it and the order is
 *                     exactly what it always was.
 */
export function orderEntriesChronologically(
  entries: readonly LogbookEntry[],
  flightById?: ReadonlyMap<string, Flight>,
): LogbookEntry[] {
  return [...entries].sort((a, b) => compareEntriesChronologically(a, b, flightById));
}
